'use client';

/**
 * Shared UI primitives for the /start funnel: ProgressBar, OptionCard,
 * FormField, PrimaryButton, BackArrow, QuizHero, TrustBadgeRow.
 *
 * No browser-API reads at render. No animation libraries. CSS
 * transitions + keyframes (defined in globals.css under
 * Track 5.Y-cat-quiz-amplification Phase 2) only.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { ArrowLeft, Check, type LucideIcon } from 'lucide-react';
import { hexToRgba } from './quiz-theme';
export { TrustBadgeRow } from '@/components/TrustBadgeRow';
import {
  applyStepOverrideSerialized,
  type SerializedQuizOverrides,
} from '@/lib/quiz/types';

// ── Theme context ────────────────────────────────────────────────────

/**
 * Phase 2: per-step theme color flows down via context so OptionCards
 * inside Steps.tsx don't need a prop-drilled `themeColor`. Default is
 * brand orange — matches StepShell/PrimaryButton baseline if no
 * provider wraps the tree (e.g. snapshot tests, deferred BiometricsStep).
 */
// Track 5.Z3 (2026-05-12): default theme demoted from brand orange to
// navy. Orange is reserved for primary CTAs + price emphasis now.
const QuizThemeContext = createContext<string>('#0f1e3d');

export function QuizThemeProvider({ themeColor, children }: { themeColor: string; children: ReactNode }) {
  return <QuizThemeContext.Provider value={themeColor}>{children}</QuizThemeContext.Provider>;
}

export function useQuizTheme(): string {
  return useContext(QuizThemeContext);
}

// ── ProgressBar ──────────────────────────────────────────────────────

/**
 * Phase 2 (Lock 5d) replaces segment-based progress with a single thin
 * gradient track (orange → amber) + circular completion markers above
 * it. Completed markers carry a check; active marker is the live ring;
 * upcoming markers are subtle gray dots. Theme-aware via `themeColor`.
 */
export function ProgressBar({
  current,
  total,
  themeColor = '#ff9100',
}: {
  /** 1-based index of the active question step. */
  current: number;
  total: number;
  /** Hex of the active step's accent. Bar end + active dot tint pull
   *  from this; completed dots stay neutral amber for consistency
   *  across theme transitions. */
  themeColor?: string;
}) {
  const safeTotal = Math.max(total, 1);
  const fraction = Math.min(Math.max((current - 0.5) / safeTotal, 0), 1);
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={`Step ${current} of ${total}`}
      className="w-full"
    >
      {/* Gradient bar */}
      <div className="relative h-1 w-full bg-slate-200 rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-300"
          style={{
            width: `${fraction * 100}%`,
            background: `linear-gradient(90deg, #0f1e3d 0%, ${themeColor} 100%)`,
          }}
        />
      </div>
      {/* Step markers: only render when total ≤ 14 to avoid visual
          clutter on the lead-form long tail. Conditional menstrual
          step keeps total ≤ 14 in practice. */}
      {total <= 14 && (
        <div className="mt-2 flex items-center justify-between">
          {Array.from({ length: total }, (_, i) => {
            const stepNum = i + 1;
            const completed = stepNum < current;
            const active = stepNum === current;
            return (
              <span
                key={stepNum}
                className={[
                  'h-3 w-3 rounded-full flex items-center justify-center transition-all',
                  completed ? 'bg-amber-400 text-white' :
                  active ? 'ring-2' :
                  'bg-slate-200',
                ].join(' ')}
                style={active ? { backgroundColor: themeColor, color: '#fff', boxShadow: `0 0 0 3px ${hexToRgba(themeColor, 0.18)}` } : undefined}
              >
                {completed && <Check size={9} strokeWidth={3.5} aria-hidden />}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── OptionCard ───────────────────────────────────────────────────────

interface OptionCardProps {
  label: string;
  description?: string;
  selected: boolean;
  onClick: () => void;
  /** Multi-select shows a checkmark instead of plain selected styling. */
  multiSelect?: boolean;
  disabled?: boolean;
  /** Optional inline icon on the left (Lucide component). */
  icon?: ReactNode;
}

export function OptionCard({
  label, description, selected, onClick, multiSelect, disabled, icon,
}: OptionCardProps) {
  // Phase 2: theme color flows from QuizThemeContext (set per step in
  // StartFlowClient). Falls back to brand orange if no provider wraps.
  const themeColor = useQuizTheme();
  // Single-line cards vertically center the icon circle with the
  // label; multi-line cards (label + description) top-align so the
  // icon sits with the first text line. Description presence is the
  // signal.
  const align = description ? 'items-start' : 'items-center';
  // Phase 2 (Lock 5b/c): hover lift on unselected cards; selected
  // state carries themeColor border + tint background + soft outer
  // ring, plus a one-shot scale-bounce keyframe (defined in
  // globals.css). The bounce only triggers on transition INTO selected
  // — re-clicks do nothing visually, which is fine.
  const selectedStyle = selected
    ? {
        borderColor: themeColor,
        backgroundColor: hexToRgba(themeColor, 0.07),
        boxShadow: `0 0 0 3px ${hexToRgba(themeColor, 0.18)}`,
      }
    : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={selectedStyle}
      className={[
        'w-full text-left rounded-xl border-2 px-4 py-4 transition-all duration-200',
        `flex ${align} gap-3 min-h-[64px]`,
        disabled ? 'opacity-50 cursor-not-allowed border-slate-200 bg-white' :
          selected ? 'animate-quiz-card-bounce' :
          'border-slate-200 bg-white hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-md',
      ].join(' ')}
    >
      {(icon || multiSelect) && (
        <div
          className={[
            // Lock 5f: bumped from h-8 w-8 (32×32) to h-10 w-10 (40×40)
            // for the premium card thumbnail; icon sized to 22px to
            // sit comfortably inside the larger bubble.
            'shrink-0 h-10 w-10 rounded-full flex items-center justify-center transition-colors',
          ].join(' ')}
          style={selected
            ? { backgroundColor: themeColor, color: '#fff' }
            : { backgroundColor: hexToRgba(themeColor, 0.10), color: themeColor }
          }
        >
          {selected && multiSelect ? <Check size={18} strokeWidth={3} aria-hidden /> : icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0f2534]">{label}</div>
        {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
      </div>
    </button>
  );
}

// ── FormField ────────────────────────────────────────────────────────

interface FormFieldProps {
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  /** Inline help text under the field, neutral. */
  hint?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal';
  autoComplete?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
}

export function FormField({
  label, type = 'text', value, onChange, placeholder, required,
  error, hint, inputMode, autoComplete, min, max, step,
}: FormFieldProps) {
  // Track 5.Y2-start-quiz-polish: restyled to match the OptionCard chrome
  // — rounded-xl, 2px border, generous py-3.5 padding, brand-orange focus
  // ring (vs the prior 1px border-only color shift). Visual parity across
  // all card-list + form-field steps.
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-wide text-slate-700 mb-1.5">
        {label}{required && <span className="text-amber-600 ml-0.5">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        min={min}
        max={max}
        step={step}
        className={[
          'w-full rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-[#0f2534] outline-none',
          'transition-all duration-200 placeholder:text-slate-400',
          'focus:ring-4',
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
            : 'border-slate-200 hover:border-slate-300 focus:border-[#0f1e3d] focus:ring-[#0f1e3d]/15',
        ].join(' ')}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}

// ── PrimaryButton ────────────────────────────────────────────────────

interface PrimaryButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Phase 2 (Lock 5h): 'navy' is the original primary CTA used on
   *  question steps; 'orange' is the funnel-Add-all-style variant
   *  used on interstitial Continue buttons (matches the in-process
   *  flow's commitment register). */
  tone?: 'navy' | 'orange';
}

export function PrimaryButton({ label, onClick, disabled, loading, tone = 'orange' }: PrimaryButtonProps) {
  // Track 5.Z3 (2026-05-12): default tone flipped from 'navy' → 'orange'.
  // Pre-fix the enabled-navy CTA read as nearly identical to the
  // disabled `bg-slate-200` state — operator's "looks disabled even
  // when enabled" complaint. The enabled orange (solid brand-orange
  // fill + white text + hover lift) is now visually unambiguous.
  // Disabled state still uses slate-200/slate-400 — the contrast
  // gap orange vs. slate is much wider than navy vs. slate.
  const enabledClasses = tone === 'orange'
    ? 'bg-[#ff9100] text-white hover:bg-[#e68200] shadow-sm hover:shadow-md hover:-translate-y-0.5'
    : 'bg-[#0f2534] text-white hover:bg-[#0a1a26] shadow-sm hover:shadow-md';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={[
        'w-full rounded-full py-4 text-sm font-semibold uppercase tracking-[0.15em] transition-all',
        disabled || loading
          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
          : enabledClasses,
      ].join(' ')}
    >
      {loading ? 'Saving…' : label}
    </button>
  );
}

// ── QuizHero ─────────────────────────────────────────────────────────

/**
 * Phase 2 (Lock 5a): theme-color tinted hero band sitting above the
 * step content. 100px desktop / 80px mobile. Eyebrow icon row above
 * the step title. Pure presentational — orchestrator passes
 * `themeColor`, `Icon`, and the eyebrow string.
 */
export function QuizHero({
  themeColor,
  Icon,
  eyebrow,
}: {
  themeColor: string;
  Icon: LucideIcon;
  eyebrow: string;
}) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      style={{
        height: '80px',
        background: `linear-gradient(135deg, ${hexToRgba(themeColor, 0.20)} 0%, ${hexToRgba(themeColor, 0.06)} 100%)`,
      }}
    >
      <div className="md:!h-[100px] absolute inset-0" aria-hidden style={{ height: 'inherit' }} />
      <div className="relative h-full flex items-center px-5 md:px-6">
        <div className="flex items-center gap-2.5">
          <span
            className="h-9 w-9 rounded-full flex items-center justify-center bg-white shadow-sm"
            aria-hidden
          >
            <Icon size={18} strokeWidth={2} style={{ color: themeColor }} />
          </span>
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: themeColor }}
          >
            {eyebrow}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── BackArrow ────────────────────────────────────────────────────────

export function BackArrow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Go back"
      className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0f2534] transition-colors"
    >
      <ArrowLeft size={16} strokeWidth={2} aria-hidden />
      Back
    </button>
  );
}

// ── SkipLink ─────────────────────────────────────────────────────────

export function SkipLink({ onClick, label = 'Skip for now' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs text-slate-500 hover:text-[#0f2534] underline underline-offset-2"
    >
      {label}
    </button>
  );
}

// ── StepShell ────────────────────────────────────────────────────────

/** Common chrome for every quiz step. Track 5.Y-cat-quiz-admin Commit 1
 *  — accepts optional stepId + overrides; when both are provided, the
 *  hardcoded headline/subtitle act as fallback values for operator
 *  cosmetic overrides resolved at render time. Caller keeps the
 *  hardcoded copy inline (canonical source for fresh installs +
 *  un-overridden fields). */
export function StepShell({
  eyebrow, headline, subtitle, children, stepId, overrides,
}: {
  eyebrow?: string;
  headline: string;
  subtitle?: string;
  children: ReactNode;
  stepId?: string;
  overrides?: SerializedQuizOverrides;
}) {
  const resolvedHeadline =
    stepId && overrides
      ? applyStepOverrideSerialized(overrides, stepId, 'title', headline)
      : headline;
  const resolvedSubtitle =
    stepId && overrides && subtitle
      ? applyStepOverrideSerialized(overrides, stepId, 'subtitle', subtitle)
      : subtitle;
  return (
    <div className="space-y-6">
      <div>
        {eyebrow && (
          <p className="text-[10px] font-semibold tracking-[0.3em] text-[#6b7a8f] uppercase mb-2">
            {eyebrow}
          </p>
        )}
        <h1 className="text-3xl md:text-4xl font-normal text-[#0f2534] leading-tight tracking-tight font-[family-name:var(--font-editorial)]">
          {resolvedHeadline}
        </h1>
        {resolvedSubtitle && (
          <p className="text-sm md:text-base text-slate-600 mt-2 max-w-prose leading-relaxed">
            {resolvedSubtitle}
          </p>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}
